// MarketBrief.jsx — AI-powered daily market brief
// API calls are proxied through /api/marketbrief (Vercel serverless function).
// The Anthropic API key lives in ANTHROPIC_API_KEY (server-side only — never in the bundle).

import React, { useState, useEffect } from 'react';

// ── Design tokens (mirrored from App.js) ─────────────────────────
const V = {
  bg:     '#0a0a0c',
  bg2:    '#111114',
  bg3:    '#18181c',
  gold:   '#c9a84c',
  goldL:  '#e8c97a',
  sage:   '#7aab8a',
  rose:   '#c47a7a',
  sky:    '#7a9ec4',
  border: 'rgba(201,168,76,0.15)',
  text:   '#f0ede6',
  muted:  'rgba(240,237,230,0.5)',
  dim:    'rgba(240,237,230,0.22)',
};
const serif   = { fontFamily: "'Cormorant Garamond', serif" };
const mono    = { fontFamily: "'DM Mono', monospace" };
const jost    = { fontFamily: "'Jost', sans-serif" };
const btnGold  = { padding: '10px 22px', background: 'rgba(201,168,76,0.1)', border: '0.5px solid rgba(201,168,76,0.4)', borderRadius: '8px', color: '#e8c97a', fontSize: '13px', letterSpacing: '0.08em', cursor: 'pointer', fontWeight: 500, fontFamily: "'Jost', sans-serif", minHeight: '44px' };
const btnGhost = { padding: '8px 18px', background: 'transparent', border: `0.5px solid rgba(201,168,76,0.15)`, borderRadius: '8px', color: 'rgba(240,237,230,0.5)', fontSize: '12px', cursor: 'pointer', fontFamily: "'Jost', sans-serif", minHeight: '44px' };
const cardBase = { background: V.bg2, border: `0.5px solid ${V.border}`, borderRadius: '12px' };

// ── VIX / Fear & Greed zone definitions ──────────────────────────
const VIX_ZONES = [
  { from: 0,  to: 15, color: V.sage,      label: 'Calm — favorable for sellers' },
  { from: 15, to: 20, color: V.gold,      label: 'Elevated — trade with awareness' },
  { from: 20, to: 30, color: '#c4934c',   label: 'Volatile — size down' },
  { from: 30, to: 40, color: V.rose,      label: 'Extreme — consider staying flat' },
];
const FG_ZONES = [
  { from: 0,  to: 25, color: V.rose,  label: 'Extreme Fear' },
  { from: 25, to: 45, color: V.gold,  label: 'Fear — options may be cheap' },
  { from: 45, to: 55, color: V.dim,   label: 'Neutral' },
  { from: 55, to: 75, color: V.sage,  label: 'Greed — be patient' },
  { from: 75, to: 100,color: V.rose,  label: 'Extreme Greed — watch for reversal' },
];

function activeZone(value, zones) {
  return zones.find(z => value >= z.from && value < z.to) || zones[zones.length - 1];
}

// ── Horizontal gauge bar ──────────────────────────────────────────
function HGauge({ value, max, zones }) {
  const pct = Math.min(100, Math.max(0, (Number(value) / max) * 100));
  return (
    <div style={{ position: 'relative', height: '8px', borderRadius: '4px', overflow: 'hidden', margin: '8px 0 6px' }}>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', borderRadius: '4px', overflow: 'hidden' }}>
        {zones.map((z, i) => (
          <div key={i} style={{ flex: z.to - z.from, background: z.color, opacity: 0.55 }} />
        ))}
      </div>
      <div style={{ position: 'absolute', top: 0, bottom: 0, width: '2px', left: `calc(${pct}% - 1px)`, background: '#f0ede6', borderRadius: '1px' }} />
    </div>
  );
}

// ── Spinner ───────────────────────────────────────────────────────
const spinStyle = `@keyframes tlaw-spin { to { transform: rotate(360deg); } }`;

// ── API call (proxied through serverless function) ────────────────
async function fetchBriefFromAPI() {
  const response = await fetch('/api/marketbrief');
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || `Server error ${response.status}`);
  }
  return data;
}

// ── Main component ────────────────────────────────────────────────
export default function MarketBriefView({ onBack, onNavigate }) {
  const [brief, setBrief]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const load = async (forceRefresh = false) => {
    setLoading(true);
    setError(null);

    try {
      const today = new Date().toDateString();
      const cacheKey = 'tlaw_marketbrief';

      if (!forceRefresh) {
        try {
          const cached = localStorage.getItem(cacheKey);
          if (cached) {
            const parsed = JSON.parse(cached);
            if (parsed.date === today) {
              setBrief(parsed.data);
              setLoading(false);
              return;
            }
          }
        } catch {}
      }

      const data = await fetchBriefFromAPI();
      try {
        localStorage.setItem(cacheKey, JSON.stringify({ date: today, timestamp: Date.now(), data }));
      } catch {}
      setBrief(data);
    } catch (err) {
      setError(err.message || 'Unknown error');
    }

    setLoading(false);
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const pad = isMobile ? '20px 16px' : '40px 32px';

  // ── Loading state ───────────────────────────────────────────────
  if (loading) return (
    <div style={{ padding: pad }}>
      <style>{spinStyle}</style>
      <button style={{ ...btnGhost, marginBottom: '28px' }} onClick={onBack}>← Dashboard</button>
      <div style={{ minHeight: '50vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '16px' }}>
        <div style={{ width: '32px', height: '32px', border: `2px solid ${V.gold}`, borderTopColor: 'transparent', borderRadius: '50%', animation: 'tlaw-spin 0.8s linear infinite' }} />
        <p style={{ ...serif, fontSize: '17px', fontStyle: 'italic', color: V.muted, margin: 0 }}>
          Gathering today's market data...
        </p>
        <p style={{ ...jost, fontSize: '12px', color: V.dim, margin: 0 }}>
          This updates once each morning.
        </p>
      </div>
    </div>
  );

  // ── Error state ─────────────────────────────────────────────────
  if (error) return (
    <div style={{ padding: pad, maxWidth: '560px' }}>
      <button style={{ ...btnGhost, marginBottom: '28px' }} onClick={onBack}>← Dashboard</button>
      <div style={{ ...cardBase, border: `0.5px solid ${V.rose}`, padding: '32px 28px', textAlign: 'center' }}>
        <div style={{ fontSize: '28px', marginBottom: '14px', color: V.rose }}>⚠</div>
        <h3 style={{ ...serif, fontSize: '20px', color: V.rose, marginBottom: '12px', fontWeight: 400 }}>
          Unable to fetch today's brief.
        </h3>
        <p style={{ ...jost, fontSize: '13px', color: V.muted, lineHeight: 1.8, marginBottom: '22px' }}>
          {error}
        </p>
        <button style={{ ...btnGold }} onClick={() => load(true)}>Try Again →</button>
      </div>
    </div>
  );

  // ── Data ready ──────────────────────────────────────────────────
  if (!brief) return null;

  const spxUp = String(brief.spx?.direction).toLowerCase() === 'up';
  const spxColor = spxUp ? V.sage : V.rose;
  const vixVal = Number(brief.vix?.value) || 0;
  const fgVal  = Number(brief.fearGreed?.value) || 0;
  const vixZone = activeZone(vixVal, VIX_ZONES);
  const fgZone  = activeZone(fgVal, FG_ZONES);

  return (
    <div style={{ padding: pad, maxWidth: '800px' }}>
      <style>{spinStyle}</style>

      {/* Back */}
      <button style={{ ...btnGhost, marginBottom: '28px' }} onClick={onBack}>← Dashboard</button>

      {/* ── Header ─────────────────────────────────────────────── */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ ...jost, fontSize: '10px', letterSpacing: '0.18em', textTransform: 'uppercase', color: V.muted, marginBottom: '6px' }}>
          Daily Market Brief
        </div>
        <h1 style={{ ...serif, fontSize: isMobile ? '24px' : '28px', fontWeight: 400, color: V.gold, margin: '0 0 6px' }}>
          Good morning. Here's your market brief.
        </h1>
        <div style={{ ...jost, fontSize: '13px', color: V.muted, marginBottom: '8px' }}>{brief.date}</div>
        {brief.greeting && (
          <p style={{ ...serif, fontSize: '15px', fontStyle: 'italic', color: V.muted, margin: 0, lineHeight: 1.6 }}>{brief.greeting}</p>
        )}
      </div>

      {/* ── Today's Read ────────────────────────────────────────── */}
      {brief.lynnsRead && (
        <div style={{ ...cardBase, borderLeft: `4px solid ${V.gold}`, padding: isMobile ? '18px 20px' : '22px 28px', marginBottom: '18px' }}>
          <div style={{ ...jost, fontSize: '10px', letterSpacing: '0.2em', textTransform: 'uppercase', color: V.muted, marginBottom: '12px' }}>
            Today's Read
          </div>
          <p style={{ ...jost, fontSize: '14px', color: V.text, lineHeight: 1.9, margin: 0 }}>{brief.lynnsRead}</p>
        </div>
      )}

      {/* ── SPX ─────────────────────────────────────────────────── */}
      {brief.spx && (
        <div style={{ ...cardBase, padding: '20px 24px', marginBottom: '14px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px', flexWrap: 'wrap', gap: '8px' }}>
            <div>
              <div style={{ ...jost, fontSize: '11px', color: V.muted, marginBottom: '6px' }}>S&amp;P 500 / SPX</div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '14px', flexWrap: 'wrap' }}>
                <span style={{ ...mono, fontSize: isMobile ? '24px' : '30px', color: V.text, fontWeight: 600 }}>
                  {Number(brief.spx.price).toLocaleString()}
                </span>
                <span style={{ ...mono, fontSize: '14px', color: spxColor }}>
                  {spxUp ? '+' : ''}{brief.spx.change} ({spxUp ? '+' : ''}{brief.spx.changePercent}%)
                </span>
              </div>
            </div>
            <div style={{ padding: '4px 12px', borderRadius: '20px', background: spxUp ? 'rgba(122,171,138,0.12)' : 'rgba(196,122,122,0.12)', border: `0.5px solid ${spxColor}`, fontSize: '11px', color: spxColor, ...jost, alignSelf: 'flex-start' }}>
              {spxUp ? '▲' : '▼'} {String(brief.spx.direction || '').toUpperCase()}
            </div>
          </div>
          {brief.spx.overnightGap && (
            <p style={{ ...jost, fontSize: '13px', color: V.muted, lineHeight: 1.7, margin: 0 }}>{brief.spx.overnightGap}</p>
          )}
        </div>
      )}

      {/* ── VIX + Fear & Greed ──────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
        {brief.vix && (
          <div style={{ ...cardBase, padding: '18px 20px' }}>
            <div style={{ ...jost, fontSize: '11px', color: V.muted, marginBottom: '6px' }}>VIX — Volatility Index</div>
            <div style={{ ...mono, fontSize: '28px', color: vixZone.color, marginBottom: '2px', fontWeight: 600 }}>{vixVal}</div>
            <HGauge value={vixVal} max={40} zones={VIX_ZONES} />
            <div style={{ ...jost, fontSize: '11px', color: vixZone.color, marginBottom: '8px' }}>{vixZone.label}</div>
            {brief.vix.context && (
              <p style={{ ...jost, fontSize: '12px', color: V.muted, lineHeight: 1.6, margin: 0 }}>{brief.vix.context}</p>
            )}
          </div>
        )}
        {brief.fearGreed && (
          <div style={{ ...cardBase, padding: '18px 20px' }}>
            <div style={{ ...jost, fontSize: '11px', color: V.muted, marginBottom: '6px' }}>Fear &amp; Greed Index</div>
            <div style={{ ...mono, fontSize: '28px', color: fgZone.color, marginBottom: '2px', fontWeight: 600 }}>{fgVal}</div>
            <HGauge value={fgVal} max={100} zones={FG_ZONES} />
            <div style={{ ...jost, fontSize: '11px', color: fgZone.color, marginBottom: '8px' }}>{brief.fearGreed.label}</div>
            {brief.fearGreed.context && (
              <p style={{ ...jost, fontSize: '12px', color: V.muted, lineHeight: 1.6, margin: 0 }}>{brief.fearGreed.context}</p>
            )}
          </div>
        )}
      </div>

      {/* ── Crude + Gold + Bonds ────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: '14px', marginBottom: '14px' }}>
        {[
          { key: 'crude', label: 'Crude Oil (WTI)', unit: '/bbl', disclaimer: 'WTI crude oil futures price per barrel. Verify current prices in your brokerage.' },
          { key: 'gold',  label: 'Gold',            unit: '/oz',  disclaimer: null },
        ].map(({ key, label, unit, disclaimer }) => {
          const d = brief[key];
          if (!d) return null;
          const up = String(d.direction).toLowerCase() === 'up';
          const col = up ? V.sage : V.rose;
          return (
            <div key={key} style={{ ...cardBase, padding: '18px 20px' }}>
              <div style={{ ...jost, fontSize: '11px', color: V.muted, marginBottom: '8px' }}>{label}</div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '8px' }}>
                <span style={{ ...mono, fontSize: '22px', color: V.text, fontWeight: 500 }}>
                  ${Number(d.price).toLocaleString()}
                  <span style={{ ...jost, fontSize: '11px', color: V.dim }}>{unit}</span>
                </span>
                <span style={{ fontSize: '13px', color: col }}>{up ? '▲' : '▼'}</span>
              </div>
              {d.context && (
                <p style={{ ...jost, fontSize: '12px', color: V.muted, lineHeight: 1.6, margin: 0 }}>{d.context}</p>
              )}
              {disclaimer && (
                <p style={{ ...jost, fontSize: '11px', color: V.dim, fontStyle: 'italic', lineHeight: 1.5, margin: '8px 0 0' }}>{disclaimer}</p>
              )}
            </div>
          );
        })}
        {brief.bonds && (() => {
          const rising = String(brief.bonds.direction).toLowerCase() === 'rising';
          const col = rising ? V.rose : V.sage;
          const arrow = rising ? '▲' : '▼';
          return (
            <div style={{ ...cardBase, padding: '18px 20px' }}>
              <div style={{ ...jost, fontSize: '11px', color: V.muted, marginBottom: '10px' }}>Treasury Yields</div>
              <div style={{ marginBottom: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <span style={{ ...jost, fontSize: '12px', color: V.dim }}>10-Year</span>
                <span style={{ ...mono, fontSize: '18px', color: col, fontWeight: 500 }}>
                  {Number(brief.bonds.tenYear).toFixed(2)}%
                  <span style={{ fontSize: '12px', marginLeft: '4px' }}>{arrow}</span>
                </span>
              </div>
              <div style={{ marginBottom: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <span style={{ ...jost, fontSize: '12px', color: V.dim }}>2-Year</span>
                <span style={{ ...mono, fontSize: '18px', color: col, fontWeight: 500 }}>
                  {Number(brief.bonds.twoYear).toFixed(2)}%
                  <span style={{ fontSize: '12px', marginLeft: '4px' }}>{arrow}</span>
                </span>
              </div>
              {brief.bonds.context && (
                <p style={{ ...jost, fontSize: '12px', color: V.muted, lineHeight: 1.6, margin: 0 }}>{brief.bonds.context}</p>
              )}
            </div>
          );
        })()}
      </div>

      {/* ── Key Events ──────────────────────────────────────────── */}
      {brief.keyEvents && brief.keyEvents.length > 0 && (
        <div style={{ ...cardBase, padding: '20px 24px', marginBottom: '14px' }}>
          <div style={{ ...jost, fontSize: '10px', letterSpacing: '0.15em', textTransform: 'uppercase', color: V.muted, marginBottom: '14px' }}>
            Today's Key Events
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {brief.keyEvents.map((ev, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: isMobile ? '70px 1fr' : '90px 1fr 60px', gap: '10px', alignItems: 'start' }}>
                <span style={{ ...mono, fontSize: '11px', color: V.dim, paddingTop: '2px', lineHeight: 1.5 }}>{ev.time}</span>
                <div>
                  <div style={{ ...jost, fontSize: '13px', color: V.text, marginBottom: '3px' }}>{ev.event}</div>
                  {ev.note && (
                    <div style={{ ...jost, fontSize: '12px', color: V.muted, lineHeight: 1.5 }}>{ev.note}</div>
                  )}
                </div>
                {!isMobile && (
                  <span style={{ fontSize: '10px', padding: '2px 8px', borderRadius: '10px', textAlign: 'center',
                    background: ev.impact === 'high' ? 'rgba(196,122,122,0.12)' : ev.impact === 'medium' ? 'rgba(201,168,76,0.08)' : 'rgba(240,237,230,0.05)',
                    color: ev.impact === 'high' ? V.rose : ev.impact === 'medium' ? V.gold : V.dim,
                    ...jost, letterSpacing: '0.04em', alignSelf: 'center' }}>
                    {ev.impact}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Buffett / trader quote ───────────────────────────────── */}
      {brief.buffettRule && (
        <div style={{ ...cardBase, borderLeft: `3px solid ${V.gold}`, padding: '20px 24px', marginBottom: '20px' }}>
          <p style={{ ...serif, fontSize: '17px', fontStyle: 'italic', color: V.gold, lineHeight: 1.6, margin: 0 }}>
            "{brief.buffettRule}"
          </p>
        </div>
      )}

      {/* ── Footer ──────────────────────────────────────────────── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '12px', borderTop: `0.5px solid ${V.border}`, flexWrap: 'wrap', gap: '10px' }}>
        <p style={{ ...jost, fontSize: '11px', color: V.dim, margin: 0 }}>
          Market data sourced via AI web search and may have slight delays or variations from live brokerage prices. Always verify with ThinkorSwim before trading. This is education, not financial advice.
        </p>
        <button style={{ ...btnGhost, padding: '6px 14px', fontSize: '11px', minHeight: '36px' }} onClick={() => load(true)}>
          Refresh →
        </button>
      </div>
    </div>
  );
}
